/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card } from "../Components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/Components/ui/dialog"

import { FilePlus2 } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/Components/ui/tooltip"
import { getSupportRequestColumns, columnDisplayNames } from "@/Components/SupportRequests/support_table/columns";
import { DataTableWrapper } from "../Components/datatable/data-table";
import { useContext, useState } from "react";
import React from "react";
import { AppContext } from "@/App";
import { ColumnFiltersState } from "@tanstack/react-table";
import { useLocation } from "react-router-dom";
import Confirmation from "@/hooks/confirmation";
import PageLockProvider, { PageLockContext, PageName } from "@/Components/PageLockProvider";
import { formatDate } from "date-fns";
import HelpLink from "@/Components/ui/help-link";
import CSV_Importer from "@/Components/datatable/data_importer/csv_importer";
import SupportRequestForm from "@/Components/SupportRequests/support_table/SupportRequestForm";
import SupportPopup from "@/Components/SupportRequests/support_table/SupportPopup";
import { DeleteIcon, EditIcon } from "@/Components/ui/Icons/TooltipIcon";
import WizardProvider, { WizardContext } from "@/Components/wizard/wizardProvider";
import { getBasicPageTourViewOnlySteps, getBasicTourSteps } from "@/Components/SupportRequests/support_wizards/basic_page_tour";
import { SupportRequest, SupportRequestContext } from "@/Components/SupportRequests/SupportRequest";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";
import TutorialChecker from "@/Components/Tutorial/TutorialChecker";
import { Tutorial } from "@/lib/utils";

export type SupportRequestColumnVisibility = {
    supportRequestId: boolean
    workstream: boolean
    workPackage: boolean
    description: boolean
    raisedBy: boolean
    owner: boolean
    date: boolean
    dueDate: boolean
    rating: boolean
    requestType: boolean
    status: boolean
    notes: boolean
    displayOnDashboard: boolean
    displayOnStatusReport: boolean
    seen: boolean
    acknowledged: boolean
}

function SupportReuqestsPage() {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);
    const wizard = useContext(WizardContext);
    const zoomLevel = useResponsiveZoom();
    const isMobile = useIsMobile();

    const useInlineEditing = app.applicationUser?.userSettings?.inlineTableEditing ?? false;

    const [demoMode, setDemoMode] = useState<boolean>(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    const locationState = useLocation().state as { passedColumnFilters?: (ColumnFiltersState & { impactRating?: string, status?: string }) | undefined };
    const passedColumnFilters = locationState || undefined;

    const [dialogTitle, setDialogTitle] = useState("Create New Support Request");
    const [dialogTitleBarActions, setDialogTitleBarActions] = useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

    const users = app.teamMembers.concat(app.projectManagers).concat(app.sponsors);

    const [supportTableContent, setSupportTableContent] = useState<SupportRequest[]>([]);
    const [supportTableDemoContent, setSupportTableDemoContent] = useState<SupportRequest[]>([]);

    const [rowSelection, setRowSelection] = useState({});

    const [importedRows, setImportedRows] = useState<any[]>([]);
    const [clearPassedColumnFilters, setClearPassedColumnFilters] = useState<boolean>(false);

    React.useEffect(() => {
        if (importedRows.length > 0) {
            const newSupportRequests = importedRows.map((row) => {
                const newSupportRequest: SupportRequest = {
                    ...row,
                    supportRequestId: 0,
                    projectId: app.selectedProject?.id || 0,
                    userId: users.find(u => u.displayName === row.owner)?.id || "",
                    workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === row.workPackage)?.workPackageId || 0,
                    workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                };
                return newSupportRequest;
            });
            saveSupportRequests(newSupportRequests);
            setImportedRows([]);
        }
    }, [importedRows]);

    const importDataType: { name: string, type: any, example: string }[] = [
        { name: "workPackage", "type": String, example: "WP-1" },
        { name: "requestType", "type": String, example: "Resource" },
        { name: "description", "type": String, example: "Support request description" },
        { name: "rating", "type": String, example: "High" },
        { name: "status", "type": String, example: "Open" },
        { name: "date", "type": Date, example: "2026-01-23" },
        { name: "dueDate", "type": Date, example: "2026-01-27" },
        { name: "owner", "type": String, example: "John Doe" },
        { name: "notes", "type": String, example: "Additional notes" },
        { name: "displayOnDashboard", "type": Boolean, example: "true" },
        { name: "displayOnStatusReport", "type": Boolean, example: "false" }
    ];

    React.useEffect(() => {
        populateDemoData();
        if (app.authLevel !== 3) { // viewer cant see this page. Sponsor can see it but not edit it
            const down = (e: KeyboardEvent) => {
                if (e.key === "r" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault()
                    addSupportRequest();
                }
            }
            document.addEventListener("keydown", down)
            return () => document.removeEventListener("keydown", down)
        }
    }, [])

    React.useEffect(() => {
        refreshSupportRequests();
    }, [app.selectedProject]);

    // Keep track of previous support request table content for comparison
    const prevSupportRequestTableContentRef = React.useRef<SupportRequest[]>([]);

    React.useEffect(() => {
        // Skip on initial load or when refreshing all support requests
        if (prevSupportRequestTableContentRef.current.length === 0 ||
            loading ||
            supportTableContent.length !== prevSupportRequestTableContentRef.current.length) {
            prevSupportRequestTableContentRef.current = [...supportTableContent];
            return;
        }

        // Find updated support requests by comparing with previous state
        const updatedSupportRequests = supportTableContent.filter((currentSupportRequest) => {
            const prevSupportRequest = prevSupportRequestTableContentRef.current.find(a => a.supportRequestId === currentSupportRequest.supportRequestId);
            if (!prevSupportRequest) return false;

            // Compare all properties to detect changes
            return JSON.stringify(prevSupportRequest) !== JSON.stringify(currentSupportRequest);
        });

        // Save each updated support request to the server
        updatedSupportRequests.forEach(supportRequest => {
            if (supportRequest.supportRequestId > 0) { // Only update existing SupportRequests, not new ones
                updateSupportRequest(supportRequest);
            }
        });

        // Update ref with current state
        prevSupportRequestTableContentRef.current = [...supportTableContent];
    }, [supportTableContent]);

    function showFormDialog(selectedSupportRequest?: SupportRequest) {
        const action = selectedSupportRequest ? (
            <div onClick={(e) => {
                e.stopPropagation();
                if (selectedSupportRequest.supportRequestId) {
                    confirmDeleteSupportRequests([selectedSupportRequest.supportRequestId]);
                }
            }}
                className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
            </div>
        ) : null;

        setDialogTitleBarActions(action);

        setDialogTitle(selectedSupportRequest ? "Edit Support Request" : "Create New Support Request");

        setDialogContent(
            <SupportRequestForm
                saveSupportRequest={saveSupportRequest}
                supportRequest={selectedSupportRequest}
            />
        );
        setDialogOpen(true);
    }

    function showPopup(selectedSupportRequest: SupportRequest) {
        const actions = (
            <div className="flex flex-row">
                <div onClick={() => {
                    showFormDialog(selectedSupportRequest);
                }}
                    className="ml-2 p-2 rounded-md cursor-pointer hover:bg-blue-600"
                >
                    <EditIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
                <div onClick={(e) => {
                    e.stopPropagation();
                    if (selectedSupportRequest.supportRequestId) {
                        confirmDeleteSupportRequests([selectedSupportRequest.supportRequestId]);
                    }
                }}
                    className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                    <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
            </div>
        );

        setDialogTitle("Support Request Details");
        setDialogTitleBarActions(
            app.authLevel !== 3 && !lock.viewOnly ? actions : null
        );
        setDialogContent(
            <SupportPopup
                supportRequest={selectedSupportRequest}
            />
        );
        setDialogOpen(true);
    }

    function refreshSupportRequests() {
        if (app.selectedProject) {
            setLoading(true);
            // fetch actions for project
            document.body.style.cursor = 'wait';
            fetch("Raids/SupportRequests/" + app.selectedProject.id)
                .then((response) => response.json())
                .then((data) => {
                    setSupportTableContent(data);
                    setLoading(false);
                    document.body.style.cursor = 'default';
                }
                );
        }
    }

    function confirmDeleteSupportRequests(supportRequestIds: number[]) {
        setDialogTitleBarActions(null);
        setDialogTitle(supportRequestIds.length > 1 ? "Are you sure you want to delete these support request?" : "Are you sure you want to delete this support request?");
        setDialogContent(<Confirmation onConfirm={() => { deleteSupportRequests(supportRequestIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function editSupportRequest(id: number) {
        if (demoMode) {
            showFormDialog(supportTableDemoContent.find((supportRequest) => supportRequest.supportRequestId === id) || undefined);
            return;
        }
        showFormDialog(supportTableContent.find((supportRequest) => supportRequest.supportRequestId === id) || undefined);
    }

    function loadColumnVisibility(): SupportRequestColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Support Requests_columnVisibility");
        if (localStorageValue) {
            const visibiltyValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                supportRequestId: visibiltyValues.find((v) => v.columnId === "supportRequestId")?.isVisible ?? false,
                workstream: visibiltyValues.find((v) => v.columnId === "workstream")?.isVisible ?? false,
                workPackage: visibiltyValues.find((v) => v.columnId === "workPackage")?.isVisible ?? true,
                description: visibiltyValues.find((v) => v.columnId === "description")?.isVisible ?? true,
                raisedBy: visibiltyValues.find((v) => v.columnId === "raisedBy")?.isVisible ?? false,
                owner: visibiltyValues.find((v) => v.columnId === "owner")?.isVisible ?? true,
                date: visibiltyValues.find((v) => v.columnId === "date")?.isVisible ?? false,
                dueDate: visibiltyValues.find((v) => v.columnId === "dueDate")?.isVisible ?? true,
                rating: visibiltyValues.find((v) => v.columnId === "rating")?.isVisible ?? true,
                requestType: visibiltyValues.find((v) => v.columnId === "requestType")?.isVisible ?? true,
                status: visibiltyValues.find((v) => v.columnId === "status")?.isVisible ?? true,
                notes: visibiltyValues.find((v) => v.columnId === "notes")?.isVisible ?? false,
                displayOnDashboard: visibiltyValues.find((v) => v.columnId === "displayOnDashboard")?.isVisible ?? false,
                displayOnStatusReport: visibiltyValues.find((v) => v.columnId === "displayOnStatusReport")?.isVisible ?? false,
                seen: visibiltyValues.find((v) => v.columnId === "seen")?.isVisible ?? false,
                acknowledged: visibiltyValues.find((v) => v.columnId === "acknowledged")?.isVisible ?? false,
            });
        }
        return {
            supportRequestId: false,
            workstream: false,
            workPackage: true,
            description: true,
            raisedBy: false,
            owner: true,
            date: false,
            dueDate: true,
            rating: true,
            requestType: true,
            status: true,
            notes: false,
            displayOnDashboard: false,
            displayOnStatusReport: false,
            seen: false,
            acknowledged: false,
        }
    }

    function addSupportRequest() {
        setClearPassedColumnFilters(true);
        if (useInlineEditing) {
            const newSupportRequest: SupportRequest = {
                supportRequestId: 0,
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "",
                workPackage: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageName || "",
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0, // default to first workstream
                workPackageId: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageId || 0, // default to first work package
                description: "New Support Request",
                raisedBy: "",
                owner: "",
                userId: "",
                date: new Date(),
                dueDate: new Date(),
                rating: "Low",
                requestType: "Resource",
                status: "Open",
                notes: "",
                displayOnDashboard: false,
                displayOnStatusReport: false,
                seen: false,
                acknowledged: false,
            };

            saveSupportRequest(newSupportRequest);
        } else {
            showFormDialog();
        }
    }

    function saveSupportRequest(supportRequest: SupportRequest) {
        if (supportRequest.supportRequestId) {
            updateSupportRequest(supportRequest);
            return;
        }
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newSupportRequest = { ...supportRequest, supportRequestId: Math.floor(Math.random() * 1000000) };
            setSupportTableDemoContent([newSupportRequest, ...supportTableDemoContent]);
            app.showActionNotification("Success", "Support Request Added Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/SupportRequest", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(supportRequest),
        })
            .then((response) => response.json())
            .then((data) => {
                setSupportTableContent((prev) => [data, ...prev]);
                app.showActionNotification("Success", "Support Request Added Successfully!", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding support request: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function saveSupportRequests(supportRequests: SupportRequest[]) {
        if (supportRequests.length === 0) return;
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newSupportRequests = supportRequests.map(sr => ({ ...sr, supportRequestId: Math.floor(Math.random() * 1000000) }));
            setSupportTableDemoContent([...newSupportRequests, ...supportTableDemoContent]);
            app.showActionNotification("Success", `${supportRequests.length} Support Requests added from CSV`, "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/SupportRequests", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(supportRequests),
        })
            .then((response) => response.json())
            .then((data) => {
                setSupportTableContent((prev) => [...data, ...prev]);
                app.showActionNotification("Success", "Support Requests Added Successfully!", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding support requests: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function updateSupportRequest(supportRequest: SupportRequest) {

        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newSupportRequest = { ...supportRequest, supportRequestId: Math.floor(Math.random() * 1000000) };
            setSupportTableDemoContent((prev) =>
                prev.map((sr) => (sr.supportRequestId === supportRequest.supportRequestId ? newSupportRequest : sr))
            );
            app.showActionNotification("Success", "Support Request Updated Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch(`/Raids/SupportRequest/${supportRequest.supportRequestId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(
                {
                    supportRequestId: supportRequest.supportRequestId,
                    projectId: supportRequest.projectId,
                    workstream: supportRequest.workstream,
                    workPackage: supportRequest.workPackage,
                    workstreamId: supportRequest.workstreamId,
                    workPackageId: supportRequest.workPackageId,
                    description: supportRequest.description,
                    raisedBy: supportRequest.raisedBy,
                    owner: supportRequest.owner,
                    userId: supportRequest.userId,
                    date: formatDate(supportRequest.date, "yyyy-MM-dd"),
                    dueDate: formatDate(supportRequest.dueDate, "yyyy-MM-dd"),
                    rating: supportRequest.rating,
                    requestType: supportRequest.requestType,
                    status: supportRequest.status,
                    notes: supportRequest.notes,
                    displayOnDashboard: supportRequest.displayOnDashboard,
                    displayOnStatusReport: supportRequest.displayOnStatusReport,
                    seen: supportRequest.seen,
                    acknowledged: supportRequest.acknowledged
                }
            ),
        })
            .then((response) => {
                if (response.ok) {
                    if (!useInlineEditing) {
                        // If not using inline editing, update the specific support request in state
                        setSupportTableContent((prev) =>
                            prev.map((r) => (r.supportRequestId === supportRequest.supportRequestId ? supportRequest : r))
                        );
                    }
                    app.showActionNotification("Success", "Support Request Updated Successfully!", "success");
                } else {
                    app.showActionNotification("Error", "Error updating support request", "destructive");
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating support request: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function deleteSupportRequests(ids: number[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setSupportTableDemoContent((prev) => prev.filter((sr) => !ids.includes(sr.supportRequestId)));
            app.showActionNotification("Success", "Support Request Deleted", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/SupportRequests", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ids),
        })
            .then(() => {
                setSupportTableContent(prev =>
                    prev.filter((sr) => !ids.includes(sr.supportRequestId))
                );
                app.showActionNotification("Success", "Support Request Deleted", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error deleting support request: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function getGroupActions() {
        const groupActions = app.authLevel > 3 ? [{
            name: "Delete",
            action: (selectedRows: any[]) => {
                confirmDeleteSupportRequests(selectedRows.map((row) => row.supportRequestId));
            },
        }] : [];

        groupActions.push(
            {
                name: "Set Status to Open",
                action: (selectedRows: any[]) => {
                    const supportRequestStatus = selectedRows.map((row) => ({ "id": row.supportRequestId, "status": "Open" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(supportRequestStatus);
                },
            },
            {
                name: "Set Status to Closed",
                action: (selectedRows: any[]) => {
                    const supportRequestStatus = selectedRows.map((row) => ({ "id": row.supportRequestId, "status": "Closed" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(supportRequestStatus);
                },
            },
            {
                name: "Set Status to Cancelled",
                action: (selectedRows: any[]) => {
                    const supportRequestStatus = selectedRows.map((row) => ({ "id": row.supportRequestId, "status": "Cancelled" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(supportRequestStatus);
                },
            });
        return groupActions;
    }

    function setStatus(supportRequestStatus: { id: number, status: "Closed" | "Cancelled" | "Open" }[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setSupportTableDemoContent(supportTableDemoContent.map((supportRequest) => {
                const updatedSupportRequest = supportRequestStatus.find((srs) => srs.id === supportRequest.supportRequestId);
                if (updatedSupportRequest) {
                    supportRequest.status = updatedSupportRequest.status;
                }
                return supportRequest;
            }));
            app.showActionNotification("Success", "Status Updated", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/SupportRequestStatus", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(supportRequestStatus),
        })
            .then(() => {
                setSupportTableContent(supportTableContent.map((supportRequest) => {
                    const updatedSupportRequest = supportRequestStatus.find((srs) => srs.id === supportRequest.supportRequestId);
                    if (updatedSupportRequest) {
                        supportRequest.status = updatedSupportRequest.status;
                    }
                    return supportRequest;
                }));
                app.showActionNotification("Success", "Status Updated", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating status: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function populateDemoData() {
        const demoSupportRequests: SupportRequest[] = [];
        const owners = users.length > 0 ? users.map(u => u.displayName) : ["User 1", "User 2", "User 3"];
        const workPackages = app.workPackageOptions?.flatMap(w => w.workPackageNames).map(wp => wp.workPackageName) || ["WP-1", "WP-2", "WP-3"];
        const requestTypes = ["Resource", "Financial", "Other"];
        const ratings = ["Low", "Medium", "High"];
        for (let i = 0; i < 20; i++) {
            const randomOwner = owners[Math.floor(Math.random() * owners.length)];
            const randomRaisedBy = owners[Math.floor(Math.random() * owners.length)];
            const randomWorkPackage = workPackages[Math.floor(Math.random() * workPackages.length)];
            const randomRequestType = requestTypes[Math.floor(Math.random() * requestTypes.length)];
            const randomRating = ratings[Math.floor(Math.random() * ratings.length)];
            const newSupportRequest: SupportRequest = {
                supportRequestId: i + 1,
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "Default Stream",
                workPackage: randomWorkPackage,
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === randomWorkPackage)?.workPackageId || 0,
                description: `This is a demo support request number ${i + 1}`,
                raisedBy: randomRaisedBy,
                owner: randomOwner,
                userId: users.find(u => u.displayName === randomOwner)?.id?.toString() || "",
                date: new Date(new Date().setDate(new Date().getDate() - Math.floor(Math.random() * 30))),
                dueDate: new Date(new Date().setDate(new Date().getDate() + Math.floor(Math.random() * 60))),
                rating: randomRating,
                requestType: randomRequestType,
                status: Math.random() < 0.7 ? "Open" : (Math.random() < 0.5 ? "Closed" : "Cancelled"),
                notes: Math.random() < 0.5 ? `These are some notes for support request number ${i + 1}` : "",
                displayOnDashboard: Math.random() < 0.3,
                displayOnStatusReport: Math.random() < 0.3,
                seen: Math.random() < 0.5,
                acknowledged: Math.random() < 0.5,
            };
            demoSupportRequests.push(newSupportRequest);
        }
        setSupportTableDemoContent(demoSupportRequests);
    }

    const demoButton = (
        <TooltipProvider delayDuration={1}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button id="exitDemoMode" className="rounded-md text-white bg-red-500 h-min px-2 my-auto" onClick={() => setDemoMode(false)}>Exit Demo Mode</button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col bg-background">
                    <div className="flex flex-row text-muted-foreground">
                        <p>
                            Demo mode allows you to try out the Support Requests page without making any actual changes to the data.
                        </p>
                        <p>
                            The table will be populated with some sample data and you can try out adding, editing, deleting and exporting Support Requests.
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const contextValue = {
        dialogOpen,
        setDialogOpen,
        supportTableDemoContent,
        setSupportTableDemoContent,
        showPopup,
        demoMode,
        setDemoMode,
        rowSelection,
        setRowSelection,
        addSupportRequest,
    };

    if (isMobile) {
        return (
            <SupportRequestContext.Provider value={contextValue}>
                <Card className="mb-8 relative" style={{ zoom: zoomLevel }}>
                    {wizard && wizard.getWizardPointer()}
                    <div className="flex flex-row bg-table-support justify-between px-2 rounded-t-lg">
                        <div className="flex flex-row">
                            <h1 className="text-4xl my-auto p-2">Support Requests</h1>
                            <div className="my-auto" onClick={refreshSupportRequests}>{lock.getRefreshControl()}</div>
                        </div>
                        <div className="flex flex-row">
                            {app.authLevel !== 3 && !lock.viewOnly ? (
                                <div className="flex flex-row justify-end">
                                    <TooltipProvider delayDuration={1}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    id="addNewSupportRequest"
                                                    onClick={() => addSupportRequest()}
                                                    className="rounded-full bg-table-support border border-white text-table-header-foreground p-2 hover:cursor-pointer m-2"
                                                >
                                                    <FilePlus2 className="w-8 h-8" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="flex flex-col bg-background">
                                                <div className="flex flex-row text-muted-foreground place-content-between">
                                                    <span className="p-2">Create New Support Request</span>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            ) : <div></div>}
                        </div>
                        <div className="flex flex-row absolute top-0 right-2 -translate-y-[130%]">{app.authLevel !== 3 && lock.getLockControl()}</div>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent id="support-dialog" className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }} onInteractOutside={(e) => {
                            if (demoMode) {
                                e.preventDefault();
                            }
                        }}>
                            <DialogHeader>
                                <DialogTitle><div className="flex"><div className="text-4xl my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                            </DialogHeader>
                            {dialogContent}
                        </DialogContent>
                    </Dialog>
                    <DataTableWrapper
                        columns={getSupportRequestColumns(
                            (ids: number[]) => confirmDeleteSupportRequests(ids),
                            (id: number) => editSupportRequest(id),
                        )}
                        data={demoMode ? supportTableDemoContent : supportTableContent}
                        setData={demoMode ? setSupportTableDemoContent : setSupportTableContent}
                        loading={loading}
                        viewOnly={app.authLevel === 3 || lock.viewOnly}
                        header_colour="bg-table-support"
                        table_title="Support Requests"
                        columnVisibility={loadColumnVisibility()}
                        columnDisplayNames={columnDisplayNames}
                        passedColumnFilters={!clearPassedColumnFilters ? passedColumnFilters as ColumnFiltersState : undefined}
                        groupActions={getGroupActions()}
                        onRowClick={!useInlineEditing ? (row) => showPopup(row.original as SupportRequest) : undefined}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                    />
                </Card>
            </SupportRequestContext.Provider>
        );
    }

    return (
        <SupportRequestContext.Provider value={contextValue}>
            <Card className="p-2 m-2 relative" style={{ zoom: zoomLevel }}>
                {wizard && wizard.getWizardPointer()}
                <div className="flex flex-row justify-between">
                    <div className="flex flex-row"><h1 className="text-2xl my-auto p-2">Support Requests</h1><div className="my-auto" onClick={refreshSupportRequests}>{lock.getRefreshControl()}</div></div>
                    <div className="flex flex-row">{app.authLevel !== 3 && lock.getLockControl()}<HelpLink page="support" />{demoMode && demoButton}</div>
                    {app.authLevel !== 3 && !lock.viewOnly ? ( // viewer cant see this page. Sponsor can see it but not edit it
                        <div className="flex flex-row justify-end">
                            <TooltipProvider delayDuration={1}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            id="addNewSupportRequest"
                                            onClick={() => addSupportRequest()}
                                            className="rounded-full bg-table-support text-table-header-foreground w-min p-2 hover:cursor-pointer m-2"
                                        >
                                            <FilePlus2 />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="flex flex-col bg-background">
                                        <div className="flex flex-row text-muted-foreground place-content-between">
                                            <span className="p-2">Create New Support Request</span>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            {<CSV_Importer propertyTypes={importDataType} setNewRows={setImportedRows} bgColor="bg-table-support" title="Import Support Requests from CSV" />}
                        </div>
                    ) : <div></div>}
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent id="support-dialog" className="flex flex-col max-w-fit" onInteractOutside={(e) => {
                        if (demoMode) {
                            e.preventDefault();
                        }
                    }}>
                        <DialogHeader>
                            <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
                <TutorialChecker
                    tutorialId={Tutorial.RAIDS}
                    tutorial={async () => {
                        setDemoMode(true);
                        const acquiredLock = await lock.lockPage();
                        wizard.setWizardSteps(getBasicPageTourViewOnlySteps({ viewOnly: false, supportContext: contextValue }));
                        wizard.setGuidedTourCompleteFunctionCall(() => () => { lock.unlockPage(acquiredLock?.id); setDemoMode(false); });
                        wizard.startGuidedTour();
                    }} />
                <DataTableWrapper
                    columns={getSupportRequestColumns(
                        (ids: number[]) => confirmDeleteSupportRequests(ids),
                        (id: number) => editSupportRequest(id),
                    )}
                    data={demoMode ? supportTableDemoContent : supportTableContent}
                    setData={demoMode ? setSupportTableDemoContent : setSupportTableContent}
                    loading={loading}
                    viewOnly={app.authLevel === 3 || lock.viewOnly} // viewer can see but not edit
                    header_colour="bg-table-support"
                    table_title="Support Requests"
                    columnVisibility={loadColumnVisibility()}
                    columnDisplayNames={columnDisplayNames}
                    passedColumnFilters={!clearPassedColumnFilters ? passedColumnFilters as ColumnFiltersState : undefined}
                    groupActions={getGroupActions()}
                    onRowClick={!useInlineEditing ? (row) => showPopup(row.original as SupportRequest) : undefined}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                />
                <div className="flex flex-row w-min ml-auto">{wizard.getTourControls()}</div>
            </Card>
        </SupportRequestContext.Provider>
    );
}

function SupportReuqests() {
    const app = useContext(AppContext);
    return <PageLockProvider pageName={PageName.SupportRequests} userId={app.applicationUser?.id as string} projectId={app.selectedProject?.id as number} >
        <WizardProvider wizardSteps={getBasicTourSteps()} >
            <SupportReuqestsPage />
        </WizardProvider>
    </PageLockProvider>
}

export default SupportReuqests;